import { useEffect, useCallback, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ActionCreatorWithoutPayload } from "@reduxjs/toolkit";

const ACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const TOKEN_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const TOKEN_REFRESH_THRESHOLD = 9 * 60 * 1000; // Refresh at 9 minutes
const ACTIVITY_THROTTLE_MS = 5000; // 5 seconds - limit timer resets
const ACTIVITY_EVENTS = ["mousedown", "keypress", "touchstart", "click"];

type TokenSelector<T = any> = (state: T) => { token: string };
type TokenRefreshFunction = () => Promise<void>;

export const useJwtExpiry = <T = any>(
  tokenSelector: TokenSelector<T>,
  resetAuthAction: ActionCreatorWithoutPayload<string>,
  refreshToken?: TokenRefreshFunction
) => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Track last active time
  const lastActiveTime = useRef<number>(Date.now());
  const lastActivityUpdate = useRef<number>(0);

  // Timers
  const activityTimer = useRef<NodeJS.Timeout | null>(null);
  const tokenTimer = useRef<NodeJS.Timeout | null>(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token } = useSelector(tokenSelector);

  const handleLogout = useCallback(() => {
    // Check if already on login page to prevent duplicate alerts
    const currentPath = window.location.pathname;
    if (currentPath.includes("/login") || isLoggingOut) return;

    setIsLoggingOut(true);

    // Clear all timers
    if (activityTimer.current) clearTimeout(activityTimer.current);
    if (tokenTimer.current) clearTimeout(tokenTimer.current);

    dispatch(resetAuthAction());
    sessionStorage.clear();
    alert("Session expired. Please login again.");
    navigate("/login");

    setTimeout(() => setIsLoggingOut(false), 1000);
  }, [dispatch, navigate, isLoggingOut, resetAuthAction]);

  const resetActivityTimer = useCallback(() => {
    // Resetting activity timer (10 minutes)
    if (activityTimer.current) {
      clearTimeout(activityTimer.current);
    }

    activityTimer.current = setTimeout(() => {
      // ACTIVITY TIMEOUT - logging out
      handleLogout();
    }, ACTIVITY_TIMEOUT);
  }, [handleLogout]);

  const resetTokenTimer = useCallback(
    (newToken?: string) => {
      // Resetting token timer (10 minutes)

      if (tokenTimer.current) {
        clearTimeout(tokenTimer.current);
      }

      // Store the start time for this timer
      const startTime = Date.now();
      sessionStorage.setItem("hash", startTime.toString());

      // Update AuthPrev if provided (for refresh scenarios)
      if (newToken) {
        sessionStorage.setItem("AuthPrev", newToken);
      }

      // Set timer for 9 minutes to refresh token
      tokenTimer.current = setTimeout(
        () => {
          if (refreshToken && !isRefreshing && !isLoggingOut) {
            //Token refresh time - attempting refresh
            setIsRefreshing(true);

            refreshToken()
              .then(() => {
                // Token refresh successful. Save token to sessionStorage
                // Note: don't call resetTokenTimer here - the effect will handle it
                // when it detects the new token
              })
              .catch((error) => {
                console.error("❌ Token refresh failed:", error);
                handleLogout();
              })
              .finally(() => {
                setIsRefreshing(false);
              });
          } else {
            // If no refresh function, logout at 10 minutes
            handleLogout();
          }
        },
        refreshToken ? TOKEN_REFRESH_THRESHOLD : TOKEN_TIMEOUT
      );
    },
    [refreshToken, isRefreshing, isLoggingOut, handleLogout]
  );

  const handleUserActivity = useCallback(() => {
    const now = Date.now();

    // Always update the activity time for accurate inactivity detection
    lastActiveTime.current = now;

    // But only reset timer if throttle period has passed to prevent excessive calls
    if (now - lastActivityUpdate.current >= ACTIVITY_THROTTLE_MS) {
      lastActivityUpdate.current = now;
      // User activity detected - resetting activity timer (throttled)
      resetActivityTimer();
    }
  }, [resetActivityTimer]);

  // Initialize activity timer and listeners only once when hook mounts
  useEffect(() => {
    // Initialize timestamps
    lastActiveTime.current = Date.now();
    lastActivityUpdate.current = Date.now();

    // Set up activity timer (only once)
    resetActivityTimer();

    // Set up activity listeners
    ACTIVITY_EVENTS.forEach((eventType) => {
      document.addEventListener(eventType, handleUserActivity, {
        passive: true,
      });
    });

    // Cleanup function
    return () => {
      // Clear activity timer
      if (activityTimer.current) {
        clearTimeout(activityTimer.current);
      }

      // Remove activity listeners
      ACTIVITY_EVENTS.forEach((eventType) => {
        document.removeEventListener(eventType, handleUserActivity);
      });
    };
  }, []); // Empty dependency array - runs only once

  // Helper to trigger token refresh
  const triggerTokenRefresh = useCallback(() => {
    if (!refreshToken || isRefreshing || isLoggingOut) {
      if (!refreshToken) handleLogout();
      return;
    }

    setIsRefreshing(true);
    refreshToken()
      .catch((error) => {
        console.error("❌ Token refresh failed:", error);
        handleLogout();
      })
      .finally(() => setIsRefreshing(false));
  }, [refreshToken, isRefreshing, isLoggingOut, handleLogout]);

  // Helper to restore timer with remaining time
  const restoreTimerWithRemainingTime = useCallback(
    (startTime: number) => {
      const elapsed = Date.now() - startTime;
      const timeout = refreshToken ? TOKEN_REFRESH_THRESHOLD : TOKEN_TIMEOUT;
      const remaining = timeout - elapsed;

      if (remaining > 0) {
        if (tokenTimer.current) clearTimeout(tokenTimer.current);
        tokenTimer.current = setTimeout(triggerTokenRefresh, remaining);
      } else {
        triggerTokenRefresh();
      }
    },
    [refreshToken, triggerTokenRefresh]
  );

  // Handle token timer - only initialize once or when token actually changes
  // Use sessionStorage to persist state across component remounts during navigation
  useEffect(() => {
    if (!token || token === "none") {
      if (tokenTimer.current) clearTimeout(tokenTimer.current);
      sessionStorage.removeItem("AuthPrev");
      sessionStorage.removeItem("hash");
      return;
    }

    if (isRefreshing) {
      return;
    }

    const AuthPrev = sessionStorage.getItem("AuthPrev");
    const startTimeStr = sessionStorage.getItem("hash");
    const isNewToken = AuthPrev !== token;
    const hasExistingTimer = !!startTimeStr;

    // Case 1: First initialization or token changed (after refresh)
    if (!hasExistingTimer || isNewToken) {
      resetTokenTimer(token);
      return;
    }

    // Case 2: Hook remounted (navigation) - restore timer with remaining time
    restoreTimerWithRemainingTime(parseInt(startTimeStr, 10));
  }, [token, resetTokenTimer, isRefreshing, restoreTimerWithRemainingTime]);

  return {
    isRefreshing,
    isLoggingOut,
    lastActiveTime: lastActiveTime.current,
  };
};
